import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TripRateCalculationViewModel from "../ViewModels/TripRateCalculationViewModel";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import TripRateTableRow from "./TripRateTableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import TripRateDay from "../ViewModels/TripRateDay";

interface IProps {
    loading: boolean;
    tripRateCalculation: TripRateCalculationViewModel;
    extrapolate: boolean;
    extrapolateValue: number;
    timeSliceStart: number;
    timeSliceEnd: number;
}

const splitHeadingAcrossTwoLines = (heading: string | null) => {
    const words = heading?.trim().split(/\s+/).filter(Boolean) ?? [];

    if (words.length < 2) {
        return words;
    }

    let bestSplit = 1;
    let smallestLongestLine = Number.POSITIVE_INFINITY;

    for (let index = 1; index < words.length; index++) {
        const firstLineLength = words.slice(0, index).join(" ").length;
        const secondLineLength = words.slice(index).join(" ").length;
        const longestLine = Math.max(firstLineLength, secondLineLength);

        if (longestLine < smallestLongestLine) {
            bestSplit = index;
            smallestLongestLine = longestLine;
        }
    }

    return [words.slice(0, bestSplit).join(" "), words.slice(bestSplit).join(" ")];
};

export default function TripRateTable({ loading, tripRateCalculation, extrapolate, extrapolateValue, timeSliceStart, timeSliceEnd }: IProps) {
    const [reducedArrivals, setReducedArrivals] = useState<TripRateDay[]>([]);
    const [reducedDepartures, setReducedDepartures] = useState<TripRateDay[]>([]);
    const [reducedTotals, setReducedTotals] = useState<TripRateDay[]>([]);
    const averageHeadingLines = splitHeadingAcrossTwoLines(tripRateCalculation.abbreviation);

    useEffect(() => {
        const tempArrivals: TripRateDay[] = [];
        const tempDepartures: TripRateDay[] = [];
        const tempTotals: TripRateDay[] = [];
        let startPeriod = '';
        let endPeriod = '';
        for (var i = 0; i < tripRateCalculation.arrivals.length; i++) {
            if (tripRateCalculation.arrivals[i].numberOfDays == 0 || tripRateCalculation.arrivals[i].numberOfDays == null) {
                if (startPeriod === '') {
                    startPeriod = tripRateCalculation.arrivals[i].period?.split('-')[0] || '';
                }
                endPeriod = tripRateCalculation.arrivals[i].period?.split('-')[1] || '';
                if (i == tripRateCalculation.arrivals.length - 1 || (tripRateCalculation.arrivals[i + 1].numberOfDays ?? 0) > 0) {
                    const tripRateDay = new TripRateDay();
                    tripRateDay.period = `${startPeriod}-${endPeriod}`;
                    tripRateDay.tripRate = null;
                    tripRateDay.numberOfDays = null;
                    tripRateDay.averageParameter = null;
                    tempArrivals.push(tripRateDay);
                    tempDepartures.push(tripRateDay);
                    tempTotals.push(tripRateDay);
                    startPeriod = '';
                }
            }
            else {
                tempArrivals.push(tripRateCalculation.arrivals[i]);
                tempDepartures.push(tripRateCalculation.departures[i]);
                tempTotals.push(tripRateCalculation.totals[i]);
            }
        }
        setReducedArrivals(tempArrivals);
        setReducedDepartures(tempDepartures);
        setReducedTotals(tempTotals);
    }, [tripRateCalculation]);

    return (
        <TableContainer component={Paper} sx={{ width: "fit-content", maxWidth: "100%", alignSelf: "flex-start", p: 0 }}>
            <Table
                size="small"
                sx={{
                    tableLayout: "auto",
                    width: "auto",
                    "& .MuiTableCell-root": {
                        px: 1.25,
                        whiteSpace: "nowrap"
                    },
                    "& .MuiTypography-root": {
                        fontSize: extrapolate ? "0.8125rem" : undefined
                    }
                }}
            >
                <TableHead sx={{ "& .MuiTypography-root": { fontWeight: 700 } }}>
                    <TableRow>
                        <TableCell align="center">
                            <Typography variant={'body2'}>Time Period</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant={'body2'}>
                                <span style={{ whiteSpace: "nowrap" }}>Number of</span><br />Days
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant={'body2'}>
                                Ave.
                                {averageHeadingLines.map((line) => (
                                    <span key={line} style={{ display: "block", whiteSpace: "nowrap" }}>{line}</span>
                                ))}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Stack>
                                <Typography><strong>ARRIVALS</strong></Typography>
                                <Typography>
                                    Total Rate: {tripRateCalculation.arrivalsTotalRate != null && tripRateCalculation.arrivalsTotalRate.toFixed(3)}
                                </Typography>
                                <Typography>Peak: {tripRateCalculation.arrivalsPeak}</Typography>
                            </Stack>
                        </TableCell>
                        {extrapolate &&
                            <TableCell align="center" sx={{ backgroundColor: '#F5F5DC' }}>
                                <Stack>
                                    <Typography variant={'body2'}>
                                        <strong>Total: </strong>
                                        {tripRateCalculation.arrivals != null && tripRateCalculation.arrivals?.length
                                            ? (tripRateCalculation.arrivals.reduce((acc, x) => acc + (x.tripRatePreCalculation ?? 0), 0) * (extrapolateValue ?? 1)).toFixed(3)
                                            : ""}
                                    </Typography>
                                    <Typography>Estimated<br />Trip Rate</Typography>
                                </Stack>
                            </TableCell>
                        }
                        <TableCell align="center">
                            <Stack>
                                <Typography><strong>DEPARTURES</strong></Typography>
                                <Typography>
                                    Total Rate: {tripRateCalculation.departuresTotalRate != null && tripRateCalculation.departuresTotalRate.toFixed(3)}
                                </Typography>
                                <Typography>Peak: {tripRateCalculation.departuresPeak}</Typography>
                            </Stack>
                        </TableCell>
                        {extrapolate &&
                            <TableCell align="center" sx={{ backgroundColor: '#F5F5DC' }}>
                                <Stack>
                                    <Typography variant={'body2'}>
                                        <strong>Total: </strong>
                                        {tripRateCalculation.departures != null && tripRateCalculation.departures?.length
                                            ? (tripRateCalculation.departures.reduce((acc, x) => acc + (x.tripRatePreCalculation ?? 0), 0) * (extrapolateValue ?? 1)).toFixed(3)
                                            : ""}
                                    </Typography>
                                    <Typography>Estimated<br />Trip Rate</Typography>
                                </Stack>
                            </TableCell>
                        }
                        <TableCell align="center">
                            <Stack>
                                <Typography><strong>TOTALS</strong></Typography>
                                <Typography>
                                    Total Rate: {tripRateCalculation.totalsTotalRate != null && tripRateCalculation.totalsTotalRate.toFixed(3)}
                                </Typography>
                                <Typography>Peak: {tripRateCalculation.totalsPeak}</Typography>
                            </Stack>
                        </TableCell>
                        {extrapolate &&
                            <TableCell align="center" sx={{ backgroundColor: '#F5F5DC' }}>
                                <Stack>
                                    <Typography variant="body2">
                                        <strong>Total: </strong>
                                        {tripRateCalculation.totals != null && tripRateCalculation.totals.length
                                            ? (
                                                tripRateCalculation.totals.reduce(
                                                    (acc, x) => acc + (x.tripRatePreCalculation ?? 0),
                                                    0
                                                ) * (extrapolateValue ?? 1)
                                            ).toFixed(3)
                                            : ""}
                                    </Typography>
                                    <Typography>Estimated<br />Trip Rate</Typography>
                                </Stack>
                            </TableCell>
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!loading && timeSliceStart != 0 &&
                        <TableRow>
                            <TableCell colSpan={extrapolate ? 9 : 6}>
                                <Typography>Data before {`${timeSliceStart}:00`.padStart(5, "0")} deselected by time slice</Typography>
                            </TableCell>
                        </TableRow>
                    }
                    {!loading ? (
                        reducedArrivals.map((arrivalRow, index) => {
                            const departureRow = reducedDepartures[index] || {};
                            const totalRow = reducedTotals[index] || {};

                            return (
                                <TripRateTableRow
                                    key={index}
                                    period={arrivalRow.period || "-"}
                                    arrivalRow={arrivalRow}
                                    departureRow={departureRow}
                                    totalRow={totalRow}
                                    peakPeriodArrivals={tripRateCalculation.arrivalsPeak ?? ""}
                                    peakPeriodDepartures={tripRateCalculation.departuresPeak ?? ""}
                                    peakPeriodTotals={tripRateCalculation.totalsPeak ?? ""}
                                    extrapolate={extrapolate}
                                    extrapolateValue={extrapolateValue}
                                />
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={extrapolate ? 9 : 6} align="center">
                                <CircularProgress />
                            </TableCell>
                        </TableRow>
                    )}
                    {!loading && timeSliceEnd != 23 &&
                        <TableRow>
                            <TableCell colSpan={extrapolate ? 9 : 6}>
                                <Typography>Data after {`${timeSliceEnd+1}:00`.padStart(5, "0")} deselected by time slice</Typography>
                            </TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
}
